import { useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { useColumns } from '../hooks/useColumns';
import { sortReleasesByStart, colorForLane } from '../lib/releases';
import { fmtInt } from '../lib/format';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { computeRealism, type RealismStatus, type HealthKey } from '../lib/health';
import { useT, format } from '../i18n';

function fmtHealthDetail(
  _key: HealthKey,
  template: string,
  values: { plan: number; remain: number; capa: number; pct: number; diff: number },
): string {
  return format(template, values);
}

const STORY_COLOR = '#2e7d32';
const BUG_COLOR   = '#c62828';

type Stage = {
  key: string;
  label: string;
  short: string;
  stories: number;
  bugs: number;
  total: number;
  description: string;
  details?: string[];
  warning?: string;
  // Nur Ist-Stage: zwei Sub-Stages mit Time-Spent-Sichten
  subStages?: SubStage[];
};

type SubStage = {
  key: string;
  label: string;
  stories: number;
  bugs: number;
  caption: string;   // Mehrzeilige Erklärung was die Sub-Stage zeigt
};

// Pro Release ein horizontaler Funnel: vier Stufen, jeweils mit Story+Bug-
// Aufteilung. Zwischen den Stufen Δ-Annotations, die den prozentualen
// Abfall (oder Anstieg) erklären.
export function ReleaseFunnel() {
  const releaseCal = useConfig(s => s.releaseCal);
  const rows = useData(s => s.rows);
  // Team-Selector ist bidirektional an den globalen Filter gebunden:
  // Wechsel hier setzt useFilters.team — alle anderen Karten reagieren mit.
  const effectiveTeam = useFilters(s => s.team);
  const setFilters    = useFilters(s => s.set);
  const releases = useMemo(() => sortReleasesByStart(releaseCal), [releaseCal]);

  const [release, setRelease] = useState<string>(releases[0] ?? '');

  const effectiveRelease = release || releases[0] || '';
  const cols = useColumns(effectiveRelease, '', effectiveTeam);

  // Teams aus den Daten: häufigste zuerst
  const teams = useMemo(() => sortedByCount(countBy(rows, 'Team Name')).map(([n]) => n), [rows]);

  if (!releases.length || !effectiveRelease) return null;

  // Stufen aus den Säulen extrahieren. Initial-Plan-Säule fließt nicht ein,
  // weil der Funnel sich auf die 'natürlichen' Planungsphasen bezieht.
  const kapa = cols.find(c => c.key === 'kapaPlan' || c.key === 'kapa');
  const grob = cols.find(c => c.key === 'epicGrob');
  const det  = cols.find(c => c.key === 'refine');
  const ist  = cols.find(c => c.key === 'ist');
  if (!kapa || !grob || !det || !ist) return null;

  const stages: Stage[] = [
    {
      key: 'kapa',
      label: 'Kapazität',
      short: 'Kapa',
      stories: kapa.stories,
      bugs:    kapa.bugs,
      total:   kapa.stories + kapa.bugs,
      description: 'Verfügbare PT laut Kapa-Modal (Team × Monat).',
      details: kapa.detailsLines,
    },
    {
      key: 'grob',
      label: 'Grobplanung (Epic-Ebene)',
      short: 'Grob',
      stories: grob.stories,
      bugs:    grob.bugs,
      total:   grob.stories + grob.bugs,
      description: 'Σ Rough + Σ Original Estimate auf Epic-Ebene.',
      details: grob.detailsLines,
    },
    {
      key: 'det',
      label: 'Detailplanung (Refinement)',
      short: 'Detail',
      stories: det.stories,
      bugs:    det.bugs,
      total:   det.stories + det.bugs,
      description: 'Vollständiger Refinement-Plan: offene + bereits abgeschlossene Items (Item-Fix-Version). Ist-Säule ist die done-Teilmenge davon.',
      details: det.detailsLines,
      warning: det.qualityWarning,
    },
    {
      key: 'ist',
      label: 'Ist Aufwand',
      short: 'Ist',
      stories: ist.stories,
      bugs:    ist.bugs,
      total:   ist.stories + ist.bugs,
      description: 'Σ Original Estimate done Stories/Bugs im Release-Fenster.',
      details: ist.detailsLines,
      warning: ist.qualityWarning,
      subStages: [
        {
          key: 'tsOrEst',
          label: 'Time Spent + Estimate-Fallback',
          stories: ist.tsOrEstStoriesPT ?? 0,
          bugs:    ist.tsOrEstBugsPT    ?? 0,
          caption:
            `Quelle: done-Items mit eigener Fix Version → Release UND Resolved im Release-Fenster. ` +
            `Pro Item: wenn „Σ Time Spent" (Sub-Task-Aggregat) > 0 → TS-Wert, sonst Original Estimate. ` +
            `Hintergrund: in SchueCal loggt niemand direkt auf Story/Bug — alle Zeiten stehen auf Sub-Tasks und werden über Σ aggregiert.\n` +
            `Outlier-Cap: jedes Item zählt max. ${ist.itemPtCap ?? 10} PT (Estimate UND Time Spent) — entschärft Aggregat-Anomalien wie CAL-94568 (977 PT).` +
            ((ist.nIstEstCapped ?? 0) + (ist.nIstTsCapped ?? 0) > 0
              ? ` Gekappt: ${ist.nIstEstCapped ?? 0} Estimate-Item(s), ${ist.nIstTsCapped ?? 0} TS-Item(s).`
              : ` Kein Item musste gekappt werden.`) + `\n` +
            `Stories: ${ist.nTsS ?? 0} mit TS · ${ist.nEstFallbackS ?? 0} mit Estimate-Fallback · ${ist.nNeitherS ?? 0} ohne beides.\n` +
            `Bugs:    ${ist.nTsB ?? 0} mit TS · ${ist.nEstFallbackB ?? 0} mit Estimate-Fallback · ${ist.nNeitherB ?? 0} ohne beides.\n` +
            `Beste Annäherung an den echten Aufwand: wo Zeit geloggt ist, wird sie genutzt; wo nicht, der Plan-Wert.`,
        },
        {
          key: 'tsOnly',
          label: 'Nur Time Spent (ohne Fallback)',
          stories: ist.tsOnlyStoriesPT ?? 0,
          bugs:    ist.tsOnlyBugsPT    ?? 0,
          caption:
            `Quelle: done-Items mit eigener Fix Version → Release UND Resolved im Release-Fenster. ` +
            `Pro Item nur „Σ Time Spent" (Sub-Task-Aggregat). Items ohne geloggte Zeit tragen 0 bei.\n` +
            `Outlier-Cap: jedes Item zählt max. ${ist.itemPtCap ?? 10} PT.` +
            ((ist.nIstTsCapped ?? 0) > 0
              ? ` ${ist.nIstTsCapped} TS-Item(s) wurden gekappt — Aggregat-Anomalien wie CAL-94568 (977 PT) sind damit raus.`
              : ` Kein Item musste gekappt werden.`) + `\n` +
            `Stories OHNE TS-Logging: ${ist.nNoTsS ?? 0} (zählen 0). Mit TS: ${ist.nTsS ?? 0}.\n` +
            `Bugs    OHNE TS-Logging: ${ist.nNoTsB ?? 0} (zählen 0). Mit TS: ${ist.nTsB ?? 0}.\n` +
            `Reine Sicht auf das, was tatsächlich auf Sub-Tasks geloggt wurde — gut zur Plausibilitätsprüfung gegen den Plan-Wert.`,
        },
      ],
    },
  ];

  // Σ-Skala für die Bar-Breite: größter Stage-Wert ist 100 %
  const scaleMax = Math.max(1, ...stages.map(s => s.total));

  // Δ-Pairs zwischen aufeinanderfolgenden Stages
  const deltas = stages.slice(1).map((to, i) => {
    const from = stages[i];
    const drift  = to.total - from.total;
    const driftPct = from.total > 0 ? (drift / from.total) * 100 : null;
    return {
      key: `${from.key}-${to.key}`,
      fromLabel: from.short,
      toLabel:   to.short,
      drift,
      driftPct,
      hint: explainTransition(from.key, to.key, drift, driftPct),
    };
  });

  // Releases mit Lane-Farbe versehen
  const releaseColor = colorForLane(releases.indexOf(effectiveRelease));

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <TrendingDownIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="overline" color="text.secondary">Funnel</Typography>
            <Typography variant="body2" color="text.secondary">
              Pro Release: Kapazität → Grobplanung → Detailplanung → Ist · mit Δ-Annotations
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={effectiveTeam}
              label="Team"
              onChange={(e) => setFilters({ team: e.target.value })}
              startAdornment={<GroupsIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />}
            >
              <MenuItem value=""><em>Alle Teams</em></MenuItem>
              {teams.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Release</InputLabel>
            <Select value={effectiveRelease} label="Release" onChange={(e) => setRelease(e.target.value)}>
              {releases.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        {/* Filter-Anzeige */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={effectiveRelease}
            sx={{ bgcolor: `${releaseColor}1f`, color: releaseColor, fontWeight: 700 }}
          />
          {effectiveTeam
            ? <Chip icon={<GroupsIcon />} size="small" color="primary" label={`Team: ${effectiveTeam}`} sx={{ fontWeight: 700 }} />
            : <Chip icon={<GroupsIcon />} size="small" variant="outlined" label="Alle Teams" />
          }
          <FilterChips />
        </Stack>

        {/* Funnel */}
        <Stack spacing={0}>
          {stages.map((stage, i) => (
            <Box key={stage.key}>
              <FunnelStage stage={stage} scaleMax={scaleMax} />
              {i < stages.length - 1 && <DeltaRow delta={deltas[i]} />}
            </Box>
          ))}
        </Stack>

        {/* Realismus-Ampel: Dreisatz aus Restzeit, Restkapa und Restarbeit */}
        <RealismBox
          release={effectiveRelease}
          team={effectiveTeam}
          totalPlan={det.stories + det.bugs}
          doneIst={ist.stories + ist.bugs}
        />

        {/* Ist-Verbrauch im %-Vergleich zu Kapa, Rough und Detail */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="overline" color="text.secondary">Ist-Verbrauch im Vergleich</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            <b>Ist</b> = {fmtInt(ist.stories + ist.bugs)} PT (Σ Time Spent, sonst Refinement-Plan-Fallback).
            Im Verhältnis zu den drei Plan-Stufen:
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <PctChip
              label="vs. Kapa"
              pct={ist.vsKapaPct ?? null}
              base={kapa.stories + kapa.bugs}
              value={ist.stories + ist.bugs}
              hint={
                ist.vsKapaPct == null
                  ? 'Keine Kapazität gepflegt'
                  : ist.vsKapaPct < 100
                    ? 'Kapazität noch nicht ausgeschöpft.'
                    : 'Kapa-Budget überzogen — entweder mehr abgearbeitet als geplant, oder Schätzung zu niedrig.'
              }
            />
            <PctChip
              label="vs. Rough"
              pct={ist.vsRoughPct ?? null}
              base={grob.stories + grob.bugs}
              value={ist.stories + ist.bugs}
              hint={
                ist.vsRoughPct == null
                  ? 'Kein Rough Estimate auf Epic-Ebene'
                  : ist.vsRoughPct < 100
                    ? 'Realer Aufwand unter Grob-Schätzung — Roadmap war konservativ.'
                    : 'Realität übersteigt Grob-Schätzung — Epics sind teurer als ursprünglich gedacht.'
              }
            />
            <PctChip
              label="vs. Detail"
              pct={ist.vsDetailPct ?? null}
              base={det.stories + det.bugs}
              value={ist.stories + ist.bugs}
              hint={
                ist.vsDetailPct == null
                  ? 'Keine Detailplanung vorhanden'
                  : ist.vsDetailPct < 100
                    ? 'Es sind noch Items offen — Plan noch nicht voll abgearbeitet.'
                    : 'Aufwand pro Item höher als Detailschätzung — Refinement war optimistisch.'
              }
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Hinweis: Time Spent wird im Team nicht gepflegt und daher nicht ausgewertet. Der Ist-Wert pro
            geschlossenem Item kommt <b>immer aus dem Refinement-Plan</b> (SP × Faktor, sonst Original
            Estimate). So bekommt man auch ohne Time-Logging einen konsistenten Verbrauchs-Wert.
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
          Die Bar-Breite skaliert zur größten Stufe (100&nbsp;%). Bei {effectiveRelease}: <b>Kapa = {fmtInt(kapa.stories + kapa.bugs)} PT</b>,
          {' '}<b>Detailplanung = {fmtInt(det.stories + det.bugs)} PT</b>.
          Δ-Annotations zeigen relativ zur vorigen Stufe.
        </Typography>
      </CardContent>
    </Card>
  );
}

function FunnelStage({ stage, scaleMax }: { stage: Stage; scaleMax: number }) {
  const sRound = Math.round(stage.stories);
  const bRound = Math.round(stage.bugs);
  const total = sRound + bRound;
  const widthPct = total > 0 ? (total / scaleMax) * 100 : 0;
  const sPct = total > 0 ? (sRound / total) * 100 : 0;

  return (
    <Tooltip arrow title={<Box sx={{ whiteSpace: 'pre-line', fontSize: 12, maxWidth: 280 }}>{stage.description}</Box>}>
      <Box sx={{ py: 1.5, cursor: 'help' }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ width: 180, flexShrink: 0 }}>{stage.label}</Typography>
          <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, minWidth: 80 }}>
            {fmtInt(total)} PT
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Stories <b>{fmtInt(sRound)}</b> · Bugs <b>{fmtInt(bRound)}</b>
          </Typography>
        </Stack>
        <Box
          sx={{
            position: 'relative',
            width: `${widthPct}%`,
            minWidth: total > 0 ? 4 : 0,
            height: 22,
            display: 'flex',
            borderRadius: 1.5,
            overflow: 'hidden',
            border: total > 0 ? 0 : 1,
            borderColor: 'divider',
            bgcolor: total === 0 ? 'transparent' : undefined,
            transition: 'width 200ms ease',
          }}
        >
          {sRound > 0 && (
            <Box sx={{ width: `${sPct}%`, bgcolor: STORY_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
              {sPct > 12 ? fmtInt(sRound) : ''}
            </Box>
          )}
          {bRound > 0 && (
            <Box sx={{ width: `${100 - sPct}%`, bgcolor: BUG_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
              {(100 - sPct) > 12 ? fmtInt(bRound) : ''}
            </Box>
          )}
          {total === 0 && (
            <Typography variant="caption" sx={{ px: 1, color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>
          )}
        </Box>

        {/* Daten-Qualitäts-Warnung */}
        {stage.warning && (
          <Box
            sx={{
              mt: 1,
              ml: { md: 22 },
              p: 1,
              borderRadius: 1,
              border: 1,
              borderColor: '#ed6c02',
              bgcolor: '#fff4e5',
              color: '#663c00',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.5 }}>
              ⚠️ Daten-Qualität niedrig
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10.5, lineHeight: 1.35, display: 'block' }}>
              {stage.warning}
            </Typography>
          </Box>
        )}

        {/* Permanent sichtbare Erläuterung — selbsterklärend pro Stage */}
        {stage.details && stage.details.length > 0 && (
          <Box
            sx={{
              mt: 1,
              ml: { md: 22 }, // unter dem Label-Block einrücken
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                fontSize: 9,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              Was wird gezählt?
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, '& > li': { mb: 0.25 } }}>
              {stage.details.map((line, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="caption"
                  sx={{ display: 'list-item', color: 'text.secondary', fontSize: 10.5, lineHeight: 1.4 }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Zwei eingerückte Sub-Stages nur für die Ist-Säule. Beide mit
            grün-rot-Bar wie die Haupt-Stage, plus Caption darunter. */}
        {stage.subStages?.map(sub => (
          <SubStageRow key={sub.key} sub={sub} scaleMax={Math.max(stage.total, sub.stories + sub.bugs)} />
        ))}
      </Box>
    </Tooltip>
  );
}

function SubStageRow({ sub, scaleMax }: { sub: SubStage; scaleMax: number }) {
  const sRound = Math.round(sub.stories);
  const bRound = Math.round(sub.bugs);
  const total = sRound + bRound;
  const widthPct = total > 0 ? (total / Math.max(1, scaleMax)) * 100 : 0;
  const sPct = total > 0 ? (sRound / total) * 100 : 0;

  return (
    <Box sx={{ mt: 1.25, ml: { md: 4 }, pl: { md: 2 }, borderLeft: 2, borderColor: 'primary.main' }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, color: 'primary.main' }}>
          ↳ {sub.label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, minWidth: 70 }}>
          {fmtInt(total)} PT
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Stories <b>{fmtInt(sRound)}</b> · Bugs <b>{fmtInt(bRound)}</b>
        </Typography>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: `${widthPct}%`,
          minWidth: total > 0 ? 4 : 0,
          height: 18,
          display: 'flex',
          borderRadius: 1.5,
          overflow: 'hidden',
          border: total > 0 ? 0 : 1,
          borderColor: 'divider',
        }}
      >
        {sRound > 0 && (
          <Box sx={{ width: `${sPct}%`, bgcolor: STORY_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
            {sPct > 12 ? fmtInt(sRound) : ''}
          </Box>
        )}
        {bRound > 0 && (
          <Box sx={{ width: `${100 - sPct}%`, bgcolor: BUG_COLOR, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
            {(100 - sPct) > 12 ? fmtInt(bRound) : ''}
          </Box>
        )}
        {total === 0 && (
          <Typography variant="caption" sx={{ px: 1, color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontSize: 10.5, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
        {sub.caption}
      </Typography>
    </Box>
  );
}

function DeltaRow({ delta }: { delta: { fromLabel: string; toLabel: string; drift: number; driftPct: number | null; hint: string } }) {
  const sign = delta.drift > 0 ? '+' : '';
  const Icon = delta.drift > 0 ? TrendingUpIcon : delta.drift < 0 ? TrendingDownIcon : TrendingFlatIcon;
  const color = delta.drift > 0 ? '#1976d2' : delta.drift < 0 ? '#c62828' : 'inherit';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 22, py: 0.5 }}>
      <Box sx={{ width: 32, color, display: 'flex', justifyContent: 'center' }}>
        <Icon fontSize="small" />
      </Box>
      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
        {delta.fromLabel} → {delta.toLabel}: {sign}{fmtInt(delta.drift)} PT
        {delta.driftPct != null && <> · {sign}{Math.round(delta.driftPct)}&nbsp;%</>}
      </Typography>
      <Typography variant="caption" color="text.secondary">{delta.hint}</Typography>
    </Stack>
  );
}

// Kleiner Chip-Block für die Ist-Verhältnis-Anzeige im Funnel-Footer.
function PctChip({ label, pct, base, value, hint }: {
  label: string;
  pct: number | null;
  base: number;
  value: number;
  hint: string;
}) {
  const color = pct == null
    ? 'text.disabled'
    : pct > 110 ? '#c62828'
    : pct > 100 ? '#ef6c00'
    : pct > 85  ? '#388e3c'
    : pct > 0   ? '#1976d2'
    : 'text.disabled';
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ whiteSpace: 'pre-line', fontSize: 12, maxWidth: 280 }}>
          {hint}{pct != null && `\n\n${fmtInt(value)} PT / ${fmtInt(base)} PT = ${Math.round(pct)} %`}
        </Box>
      }
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 160,
          p: 1.25,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          cursor: 'help',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
        <Typography variant="h6" sx={{ color, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {pct == null ? '–' : `${Math.round(pct)} %`}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>
          {pct == null
            ? 'kein Vergleichswert'
            : `${fmtInt(value)} / ${fmtInt(base)} PT`}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function FilterChips() {
  const assignee = useFilters(s => s.assignee);
  const status   = useFilters(s => s.status);
  const priority = useFilters(s => s.priority);
  const search   = useFilters(s => s.search);
  return (
    <>
      {status   && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`Status: ${status}`} />}
      {assignee && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`Assignee: ${assignee}`} />}
      {priority && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`Priorität: ${priority}`} />}
      {search   && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`Suche: "${search}"`} />}
    </>
  );
}

// Erklärungs-Helper für die Δ-Annotation. Lasse die Heuristik bewusst kurz:
// der User soll die typische Ursache schnell verstehen, nicht eine ganze
// Story-Time darüber lesen.
function explainTransition(from: string, to: string, drift: number, driftPct: number | null): string {
  const pctAbs = driftPct == null ? 0 : Math.abs(Math.round(driftPct));
  if (driftPct == null) return 'noch keine Vergleichsbasis (eine der Stufen ist 0)';

  if (from === 'kapa' && to === 'grob') {
    if (drift < 0) return `Grobplanung deckt nur ${100 - pctAbs}% der Kapa ab — entweder Kapa ist großzügig oder Roadmap noch lückenhaft.`;
    if (drift > 0) return `Grobplanung überschreitet Kapa um ${pctAbs}% — Überbuchung, Re-Planning erwägen.`;
    return 'Grobplanung deckt die Kapa exakt ab.';
  }
  if (from === 'grob' && to === 'det') {
    if (drift < 0) return `Refinement liefert bisher nur ${100 - pctAbs}% des Rough — Stories/Bugs noch nicht alle geschnitten.`;
    if (drift > 0) return `Detailplanung übersteigt Rough um ${pctAbs}% — die Epics werden teurer als ursprünglich gedacht.`;
    return 'Rough und Σ refined sind im Gleichgewicht.';
  }
  if (from === 'det' && to === 'ist') {
    if (drift < 0) return `${100 - pctAbs}% des Refinement-Plans bereits done; ${pctAbs}% noch offen (Detail − Ist = ausstehende Arbeit).`;
    if (drift > 0) return `Ist überschreitet Plan rechnerisch — sollte nicht vorkommen (Ist ist Teilmenge von Detail). Bitte Filter prüfen.`;
    return 'Refinement-Plan komplett abgearbeitet.';
  }
  return '';
}

// ── Realismus-Ampel ──────────────────────────────────────────────────

const STATUS_COLORS: Record<RealismStatus, { bg: string; border: string; text: string }> = {
  green:       { bg: '#e6f4ea', border: '#2e7d32', text: '#1b5e20' },
  'green-light':{ bg: '#eaf5ec', border: '#43a047', text: '#2e7d32' },
  yellow:      { bg: '#fff8e1', border: '#f57c00', text: '#bf6800' },
  red:         { bg: '#fce8e6', border: '#c62828', text: '#b71c1c' },
  done:        { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
  'no-data':   { bg: 'transparent', border: '#9e9e9e', text: '#616161' },
};

function RealismBox({ release, team, totalPlan, doneIst }: {
  release: string;
  team: string;
  totalPlan: number;
  doneIst: number;
}) {
  const releaseCal     = useConfig(s => s.releaseCal);
  const capacityByTeam = useConfig(s => s.capacityByTeam);
  const t = useT();

  const r = useMemo(
    () => computeRealism({ release, releaseCal, capacityByTeam, team: team || undefined, totalPlan, doneIst }),
    [release, team, totalPlan, doneIst, releaseCal, capacityByTeam]
  );

  const c = STATUS_COLORS[r.status];
  const pct = (x: number | null) => x == null ? '–' : `${Math.round(x * 100)} %`;
  const utilRemainingPct = r.utilizationRemaining == null ? 0 : Math.round(r.utilizationRemaining * 100);
  const overhead = Math.round(r.remainingWork - r.remainingKapa);
  const health = t.health[r.healthKey];
  const detail = fmtHealthDetail(r.healthKey, health.detail, {
    plan: Math.round(r.totalPlan),
    remain: Math.round(r.remainingWork),
    capa: Math.round(r.remainingKapa),
    pct: utilRemainingPct,
    diff: overhead,
  });

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        border: 2,
        borderColor: c.border,
        bgcolor: c.bg,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Box sx={{ fontSize: 28, lineHeight: 1 }}>{r.emoji}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" sx={{ color: c.text, fontWeight: 700, lineHeight: 1, display: 'block' }}>
            {t.funnel.realismCheck}
          </Typography>
          <Typography variant="h6" sx={{ color: c.text, fontWeight: 700, lineHeight: 1.2 }}>
            {health.headline}
          </Typography>
          <Typography variant="body2" sx={{ color: c.text, mt: 0.5 }}>
            {detail}
          </Typography>
        </Box>
      </Stack>

      {r.status !== 'no-data' && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <RealismStat label="Restzeit"            value={`${r.monthsRemaining.toFixed(1)} / ${r.monthsTotal} Monate`} hint={`${Math.round(r.currentMonthProgress * 100)} % des aktuellen Monats vorbei`} />
          <RealismStat label="Restkapa"            value={`${fmtInt(r.remainingKapa)} PT`}      hint={`${fmtInt(r.pastKapa)} verbraucht / ${fmtInt(r.totalKapa)} total`} />
          <RealismStat label="Restarbeit"          value={`${fmtInt(r.remainingWork)} PT`}      hint={`${fmtInt(r.doneIst)} done / ${fmtInt(r.totalPlan)} Plan`} />
          <RealismStat
            label="Auslastung Restzeit"
            value={pct(r.utilizationRemaining)}
            hint={`Restarbeit ÷ Restkapa · Gesamt: ${pct(r.utilizationTotal)}`}
            highlight
          />
        </Stack>
      )}
    </Box>
  );
}

function RealismStat({ label, value, hint, highlight }: { label: string; value: string; hint?: string; highlight?: boolean }) {
  return (
    <Tooltip arrow title={hint ?? ''}>
      <Box
        sx={{
          flex: 1,
          minWidth: 140,
          p: 1.25,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: highlight ? 'currentColor' : 'divider',
          cursor: hint ? 'help' : 'default',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}
